import './ConfigurableOverlay.css';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { fetchOverlayRows } from '../shared/leaderboardShared';
import { decodeDraftConfig, DRAFT_PARAM, DRAFT_SLUG } from '../shared/draftConfig';
import { loadOverlayConfig, normalizeOverlayConfig, resolveOverlayMode } from './overlayConfig';

const NAME_FONT_MIN = 8;
const NAME_RIGHT_SAFETY_PX = 10;
const NAME_FIT_SAFETY_FACTOR = 0.94;
const FLAG_WIDTH_EM = 1.15;
const FLAG_MARGIN_EM = 0.3;

const ANCHOR_POSITIONS = {
    'top-left': { top: true, left: true },
    'top-center': { top: true, centerX: true },
    'top-right': { top: true, right: true },
    'middle-left': { centerY: true, left: true },
    'middle-center': { centerY: true, centerX: true },
    'middle-right': { centerY: true, right: true },
    'bottom-left': { bottom: true, left: true },
    'bottom-center': { bottom: true, centerX: true },
    'bottom-right': { bottom: true, right: true },
};

const WORD_START = new RegExp('(^|[\\s\\-_])(\\p{L})', 'gu');

function applyTextCase(text, textCase) {
    if (textCase === 'uppercase') return String(text).toUpperCase();
    if (textCase === 'capitalize') {
        return String(text).replace(WORD_START, (match, prefix, letter) => prefix + letter.toUpperCase());
    }
    return String(text);
}

function formatPoints(value, decimals) {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return '';
    return decimals > 0 ? parsed.toFixed(decimals) : String(Math.round(parsed));
}

function buildFramePosition(layout) {
    const anchor = ANCHOR_POSITIONS[layout.anchor] || ANCHOR_POSITIONS['top-right'];
    const style = {
        width: `${layout.frameWidth}px`,
        height: `${layout.frameHeight}px`,
        borderRadius: `${layout.frameRadius}px`,
    };

    if (anchor.left) style.left = `${layout.offsetX}px`;
    if (anchor.right) style.right = `${layout.offsetX}px`;
    if (anchor.centerX) {
        style.left = '50%';
        style.marginLeft = `${layout.offsetX - layout.frameWidth / 2}px`;
    }
    if (anchor.top) style.top = `${layout.offsetY}px`;
    if (anchor.bottom) style.bottom = `${layout.offsetY}px`;
    if (anchor.centerY) {
        style.top = '50%';
        style.marginTop = `${layout.offsetY - layout.frameHeight / 2}px`;
    }

    return style;
}

function OverlayRowContent({ row }) {
    if (!row.members || !row.members.length) return row.teamName;

    return row.members.map((member, index) => (
        <React.Fragment key={`${member.name}-${index}`}>
            <img
                className="cfg-overlay-flag"
                src={member.flagUrl}
                alt=""
                onError={(event) => {
                    event.currentTarget.onerror = null;
                    event.currentTarget.src = member.fallbackFlagUrl;
                }}
            />
            {member.name}
            {index < row.members.length - 1 ? ' - ' : ''}
        </React.Fragment>
    ));
}

function OverlayRow({ config, index, nameFit, row }) {
    const { display, theme } = config;
    const stateClass = row.alive ? 'is-alive' : 'is-dead';

    return (
        <div className={`cfg-overlay-row ${stateClass}`} style={{ '--row-index': `${index}` }}>
            {display.showRank && <div className="cfg-overlay-rank">{row.place || ''}</div>}
            <div className="cfg-overlay-name">
                <span
                    className="cfg-overlay-name-inner"
                    style={nameFit.scaleX < 1 ? { transform: `scaleX(${nameFit.scaleX})` } : undefined}
                >
                    {display.showAliveDot && row.alive && <span className="cfg-overlay-alive-dot" aria-hidden="true" />}
                    <span
                        className="cfg-overlay-name-text"
                        style={{
                            fontSize: `${nameFit.size}px`,
                            color: row.alive && theme.aliveNameColor ? theme.aliveNameColor : undefined,
                        }}
                    >
                        <OverlayRowContent row={row} />
                    </span>
                </span>
            </div>
            {display.showPoints && (
                <div className="cfg-overlay-points">
                    {row.teamName ? formatPoints(row.points, display.pointsDecimals) : ''}
                </div>
            )}
        </div>
    );
}

function StatusScreen({ message }) {
    return <div className="cfg-overlay-status">{message}</div>;
}

export function buildOverlayStyle(config) {
    const { display, layout, theme } = config;
    const flagRadius = display.flagShape === 'circle'
        ? '50%'
        : (display.flagShape === 'rounded' ? '3px' : '0');

    return {
        '--ov-alive': theme.aliveColor,
        '--ov-alive-dot-size': `${theme.aliveDotSize}px`,
        '--ov-alive-dot-gap': `${theme.aliveDotGap}px`,
        '--ov-dead-opacity': display.dimDeadRows ? display.deadOpacity : 1,
        '--ov-flag-radius': flagRadius,
        '--ov-font': `'${theme.fontFamily}', 'Eurostile', sans-serif`,
        '--ov-font-weight': theme.fontWeight,
        '--ov-letter-spacing': `${theme.letterSpacing}em`,
        '--ov-name-color': theme.nameColor,
        '--ov-points-color': theme.pointsColor,
        '--ov-points-font-size': `${theme.pointsFontSize}px`,
        '--ov-points-left': `${layout.pointsLeft}%`,
        '--ov-points-width': `${layout.pointsWidth}%`,
        '--ov-rank-color': theme.rankColor,
        '--ov-rank-font-size': `${theme.rankFontSize}px`,
        '--ov-rank-left': `${layout.rankLeft}%`,
        '--ov-rank-width': `${layout.rankWidth}%`,
        '--ov-row-height': `${layout.rowHeight}%`,
        '--ov-row-step': `${layout.rowStep}%`,
        '--ov-rows-start': `${layout.rowsStart}%`,
        '--ov-name-left': `${layout.nameLeft}%`,
        '--ov-name-width': `${layout.nameWidth}%`,
        '--ov-text-shadow': theme.textShadow,
        '--ov-title-font-size': `${Math.round(layout.frameHeight * 0.035)}px`,
    };
}

function ConfigurableOverlay({
    configOverride = null,
    disableFetch = false,
    previewRows = null,
    previewMode = false,
}) {
    const { slug } = useParams();
    const location = useLocation();
    const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const [loadedConfig, setLoadedConfig] = useState(null);
    const [configError, setConfigError] = useState('');
    const [rows, setRows] = useState([]);
    const [page, setPage] = useState(0);
    const [nameFits, setNameFits] = useState([]);
    const [viewportScale, setViewportScale] = useState(1);
    const rowsLayerRef = useRef(null);

    useEffect(() => {
        if (configOverride) return undefined;

        const controller = new AbortController();
        setLoadedConfig(null);
        setConfigError('');
        setPage(0);

        if (slug === DRAFT_SLUG) {
            const draft = decodeDraftConfig(searchParams.get(DRAFT_PARAM));
            try {
                if (!draft) throw new Error('Le brouillon de configuration est illisible.');
                setLoadedConfig(normalizeOverlayConfig(draft, {
                    slug: 'draft',
                    configUrl: window.location.href,
                }));
            } catch (error) {
                setConfigError(error.message);
            }
            return () => controller.abort();
        }

        loadOverlayConfig(slug, { signal: controller.signal })
            .then(setLoadedConfig)
            .catch((error) => {
                if (error.name !== 'AbortError') setConfigError(error.message);
            });

        return () => controller.abort();
    }, [configOverride, searchParams, slug]);

    const config = configOverride || loadedConfig;

    useEffect(() => {
        if (!config?.theme.fontFile || typeof FontFace === 'undefined') return undefined;
        const customFont = new FontFace(config.theme.fontFamily, `url("${config.theme.fontFile}")`);
        let registered = false;

        customFont.load()
            .then((loadedFont) => {
                document.fonts.add(loadedFont);
                registered = true;
            })
            .catch((error) => console.error('Error loading overlay font:', error));

        return () => {
            if (registered) document.fonts.delete(customFont);
        };
    }, [config?.theme.fontFamily, config?.theme.fontFile]);

    const leaderboardId = searchParams.get('id') || config?.leaderboardId || '';
    const mode = resolveOverlayMode(searchParams, config?.mode);
    const showFlags = searchParams.get('flags') === null
        ? Boolean(config?.showFlags)
        : searchParams.get('flags') === 'true';

    const maxEntries = config?.maxEntries || 50;
    const refreshDelay = ((mode === 'alive' ? config?.aliveRefreshSeconds : config?.refreshSeconds) || 10) * 1000;

    const fetchRows = useCallback(async () => {
        if (!leaderboardId || disableFetch) return;
        try {
            const data = await fetchOverlayRows({
                leaderboardId,
                showFlags,
                maxEntries: mode === 'alive' ? Number.POSITIVE_INFINITY : maxEntries,
            });
            setRows(data);
        } catch (error) {
            setRows([]);
        }
    }, [disableFetch, leaderboardId, maxEntries, mode, showFlags]);

    useEffect(() => {
        if (disableFetch || !leaderboardId) return undefined;
        let cancelled = false;
        let timeoutId;
        const delay = refreshDelay;

        const refresh = async () => {
            await fetchRows();
            if (!cancelled) timeoutId = window.setTimeout(refresh, delay);
        };
        refresh();

        return () => {
            cancelled = true;
            window.clearTimeout(timeoutId);
        };
    }, [disableFetch, fetchRows, leaderboardId, refreshDelay]);

    useEffect(() => {
        if (previewMode || !config) return undefined;
        const updateScale = () => {
            const scale = Math.min(
                window.innerWidth / config.layout.canvasWidth,
                window.innerHeight / config.layout.canvasHeight
            );
            setViewportScale(Number.isFinite(scale) && scale > 0 ? scale : 1);
        };
        updateScale();
        const observer = new ResizeObserver(updateScale);
        observer.observe(document.documentElement);
        window.addEventListener('resize', updateScale);
        return () => {
            observer.disconnect();
            window.removeEventListener('resize', updateScale);
        };
    }, [config, previewMode]);

    const sourceRows = useMemo(() => {
        const base = rows.length ? rows : (previewRows || []);
        if (!config) return base;
        if (mode === 'alive') return base.filter((row) => row.alive);
        if (mode === 'top') return base.slice(0, config.pageSize);
        return base.slice(0, config.maxEntries);
    }, [config, mode, previewRows, rows]);

    const pageSize = config?.pageSize || 10;
    const totalPages = mode === 'all' && config?.pagination.auto
        ? Math.max(1, Math.ceil(sourceRows.length / pageSize))
        : 1;

    useEffect(() => {
        if (page > totalPages - 1) setPage(0);
    }, [page, totalPages]);

    useEffect(() => {
        if (totalPages <= 1 || !config?.pagination.auto) return undefined;
        const interval = window.setInterval(() => {
            setPage((current) => (current + 1) % totalPages);
        }, config.pagination.intervalSeconds * 1000);
        return () => window.clearInterval(interval);
    }, [config?.pagination.auto, config?.pagination.intervalSeconds, totalPages]);

    const displayedRows = useMemo(() => {
        const start = page * pageSize;
        const slice = sourceRows.slice(start, start + pageSize);
        if (!config?.display.fillEmptyRows) return slice;
        return Array.from({ length: pageSize }, (_, index) => slice[index] || {
            place: '',
            teamName: '',
            points: '',
            alive: false,
            members: null,
        });
    }, [config?.display.fillEmptyRows, page, pageSize, sourceRows]);

    useEffect(() => {
        if (!config) return undefined;

        const measure = (context, text, fontSize, flagCount) => {
            context.font = `${config.theme.fontWeight} ${fontSize}px ${config.theme.fontFamily}, Eurostile, sans-serif`;
            const displayText = applyTextCase(text, config.display.textCase);
            const base = context.measureText(displayText).width;
            const spacing = Math.max(0, displayText.length - 1) * fontSize * config.theme.letterSpacing;
            const flagsWidth = flagCount * fontSize * (FLAG_WIDTH_EM + FLAG_MARGIN_EM + config.theme.letterSpacing);
            return base + spacing + flagsWidth + 2;
        };

        const recalculate = () => {
            const layer = rowsLayerRef.current;
            if (!layer) return;
            const rowElements = Array.from(layer.querySelectorAll('.cfg-overlay-row'));
            if (!rowElements.length) return;

            const context = document.createElement('canvas').getContext('2d');
            if (!context) return;

            const fits = displayedRows.map((row, index) => {
                const rowElement = rowElements[index];
                const nameElement = rowElement?.querySelector('.cfg-overlay-name');
                if (!row.teamName || !nameElement) {
                    return { size: NAME_FONT_MIN, scaleX: 1 };
                }

                const rowHeight = nameElement.clientHeight || rowElement.clientHeight || 0;
                const maxSize = rowHeight > 0
                    ? Math.max(NAME_FONT_MIN, Math.round(rowHeight * config.theme.nameFontRatio))
                    : NAME_FONT_MIN;

                const dotElement = rowElement.querySelector('.cfg-overlay-alive-dot');
                const innerElement = rowElement.querySelector('.cfg-overlay-name-inner');
                const innerStyle = innerElement ? window.getComputedStyle(innerElement) : null;
                const gapWidth = innerStyle
                    ? Number.parseFloat(innerStyle.columnGap || innerStyle.gap || '0') || 0
                    : 0;
                const dotWidth = dotElement ? dotElement.offsetWidth + gapWidth : 0;
                const nameStyle = window.getComputedStyle(nameElement);
                const paddingWidth = (Number.parseFloat(nameStyle.paddingLeft) || 0)
                    + (Number.parseFloat(nameStyle.paddingRight) || 0);
                const contentWidth = Math.max(12, nameElement.clientWidth - paddingWidth);
                const availableWidth = Math.max(12, contentWidth - dotWidth - NAME_RIGHT_SAFETY_PX);
                const flagCount = row.members ? row.members.length : 0;

                let fitted = maxSize;
                const baseWidth = measure(context, row.teamName, maxSize, flagCount);
                if (baseWidth > 0) {
                    fitted = Math.floor((availableWidth / baseWidth) * maxSize * NAME_FIT_SAFETY_FACTOR);
                }
                fitted = Math.max(NAME_FONT_MIN, Math.min(maxSize, fitted));

                while (measure(context, row.teamName, fitted, flagCount) > availableWidth && fitted > NAME_FONT_MIN) {
                    fitted -= 1;
                }

                const finalWidth = measure(context, row.teamName, fitted, flagCount);
                const scaleX = finalWidth > availableWidth
                    ? Math.max(0.4, (contentWidth - 2) / ((finalWidth + dotWidth) * 1.06))
                    : 1;

                return { size: fitted, scaleX };
            });

            setNameFits((previous) => {
                const identical = previous.length === fits.length
                    && previous.every((value, index) => value.size === fits[index].size
                        && value.scaleX === fits[index].scaleX);
                return identical ? previous : fits;
            });
        };

        const frameId = window.requestAnimationFrame(recalculate);
        const observer = new ResizeObserver(recalculate);
        if (rowsLayerRef.current) observer.observe(rowsLayerRef.current);
        if (document.fonts?.ready) document.fonts.ready.then(recalculate);
        window.addEventListener('resize', recalculate);

        return () => {
            window.cancelAnimationFrame(frameId);
            observer.disconnect();
            window.removeEventListener('resize', recalculate);
        };
    }, [config, displayedRows]);

    if (configError) return <StatusScreen message={configError} />;
    if (!config) return <StatusScreen message="Chargement de l’overlay…" />;

    const { layout, theme } = config;
    const rootClassNames = [
        'cfg-overlay-root',
        `case-${config.display.textCase}`,
        theme.aliveDotPulse ? 'has-pulse' : '',
        previewMode ? 'is-preview' : '',
    ].filter(Boolean).join(' ');

    return (
        <div
            className={rootClassNames}
            style={{
                ...buildOverlayStyle(config),
                width: `${layout.canvasWidth}px`,
                height: `${layout.canvasHeight}px`,
                transform: previewMode ? undefined : `scale(${viewportScale})`,
            }}
        >
            {config.customCss && <style>{config.customCss}</style>}
            <div className="cfg-overlay-frame" style={buildFramePosition(layout)}>
                <div
                    className="cfg-overlay-background"
                    style={{
                        backgroundColor: theme.backgroundColor,
                        backgroundImage: theme.backgroundImage ? `url("${theme.backgroundImage}")` : 'none',
                        backgroundPosition: theme.backgroundPosition,
                        backgroundSize: theme.backgroundSize,
                        borderRadius: `${layout.frameRadius}px`,
                    }}
                />
                {config.title && <div className="cfg-overlay-title">{config.title}</div>}
                <div className="cfg-overlay-rows" ref={rowsLayerRef}>
                    {displayedRows.map((row, index) => (
                        <OverlayRow
                            key={`overlay-row-${index}`}
                            config={config}
                            index={index}
                            nameFit={nameFits[index] || { size: NAME_FONT_MIN, scaleX: 1 }}
                            row={row}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
}

export default ConfigurableOverlay;
